'use client'

import { useState, useCallback, useEffect, useRef } from 'react'

type ScriptType = 'simplified' | 'traditional'

// Known high-quality voices to prioritize (Neural/Natural sounding)
const TOP_TIER_VOICES = [
  'Xiaoxiao', // Microsoft (Neural)
  'Yunxi', // Microsoft (Neural)
  'HsiaoChen', // Microsoft (Taiwan Neural)
  'Meijia', // Microsoft
  'Google pǔtōnghuà', // Google Main
  'Google 普通话', // Google CN
  'Google 國語', // Google TW
  'Google Chinese',
  'Tingting', // Apple (High quality Chinese)
  'Mei-Jia' // Apple/Siri
]

interface UseAudioOptions {
  rate?: number // Allow dynamic speed (e.g. 0.75 for slow mode)
  volume?: number
}

type PlayCallbacks = {
  onStart?: () => void
  onEnd?: () => void
  onError?: (event?: SpeechSynthesisErrorEvent | null) => void
}

export type PlayHandle = {
  cancel: () => void
  finished: Promise<void>
}

export function useAudio(
  scriptType: ScriptType = 'simplified',
  { rate = 0.85, volume = 1 }: UseAudioOptions = {}
) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentVoice, setCurrentVoice] = useState<SpeechSynthesisVoice | null>(
    null
  )
  const [stuckMessage, setStuckMessage] = useState<string | null>(null)

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)
  const playSessionRef = useRef(0)
  // Track if component is mounted to avoid state updates on unmount
  const isMounted = useRef(false)

  // 1. Robust Voice Loading
  const getVoices = useCallback((): Promise<SpeechSynthesisVoice[]> => {
    return new Promise((resolve) => {
      if (typeof window === 'undefined') {
        resolve([])
        return
      }

      const synth = window.speechSynthesis
      const voices = synth.getVoices()

      if (voices.length > 0) {
        resolve(voices)
        return
      }

      // Event listener for Chrome/Android
      const handler = () => {
        const loaded = synth.getVoices()
        if (loaded.length > 0) {
          synth.removeEventListener('voiceschanged', handler)
          resolve(loaded)
        }
      }

      synth.addEventListener('voiceschanged', handler)

      // Safety timeout
      setTimeout(() => {
        synth.removeEventListener('voiceschanged', handler)
        const fallbackVoices = window.speechSynthesis.getVoices()
        resolve(fallbackVoices)
      }, 1000)
    })
  }, [])

  // 2. The Smart Selection Logic
  const selectBestVoice = useCallback(
    (voices: SpeechSynthesisVoice[], type: ScriptType) => {
      if (!voices.length) {
        return null
      }

      const targetRegion = type === 'simplified' ? 'CN' : 'TW'
      const avoidRegion = type === 'simplified' ? 'TW' : 'CN'

      // Filter candidates
      const candidates = voices.filter((v) => {
        const lang = v.lang.replace('_', '-')
        const name = v.name.toLowerCase()

        // Must be Chinese
        if (
          !lang.startsWith('zh') &&
          !lang.startsWith('cmn') &&
          !name.includes('chinese')
        ) {
          return false
        }
        // No Cantonese
        if (
          lang.includes('HK') ||
          name.includes('cantonese') ||
          name.includes('yue')
        ) {
          return false
        }
        return true
      })

      if (!candidates.length) return null

      // Scoring
      const scored = candidates.map((voice) => {
        let score = 0
        const lang = voice.lang.replace('_', '-')
        const name = voice.name

        if (lang.includes(targetRegion)) score += 10
        if (lang.includes(avoidRegion)) score -= 5
        if (TOP_TIER_VOICES.some((goodName) => name.includes(goodName)))
          score += 20
        if (name.includes('Google') || name.includes('Microsoft')) score += 5
        if (!voice.localService) score += 2 // Prefer network/neural

        return { voice, score }
      })

      scored.sort((a, b) => b.score - a.score)

      return scored[0].voice
    },
    []
  )

  // Initialize
  useEffect(() => {
    isMounted.current = true
    if (typeof window === 'undefined') {
      return
    }
    if (!('speechSynthesis' in window)) {
      return
    }

    const init = async () => {
      const voices = await getVoices()
      if (isMounted.current) {
        const best = selectBestVoice(voices, scriptType)
        setCurrentVoice(best)
      }
    }

    init()

    // Stop audio on page unload/refresh (handles HMR and browser refresh)
    const handleBeforeUnload = () => {
      window.speechSynthesis.cancel()
    }

    // Stop audio when tab is hidden (user switches tabs)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        window.speechSynthesis.cancel()
        if (isMounted.current) setIsPlaying(false)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      isMounted.current = false
      window.speechSynthesis.cancel() // Stop talking if user leaves page
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [getVoices, selectBestVoice, scriptType])

  const stop = useCallback(() => {
    if (typeof window === 'undefined') return
    playSessionRef.current += 1
    utteranceRef.current = null
    window.speechSynthesis.cancel()
    setIsPlaying(false)
  }, [])

  const play = useCallback(
    async (
      text: string,
      callbacks: PlayCallbacks = {}
    ): Promise<PlayHandle | undefined> => {
      if (typeof window === 'undefined') {
        return
      }
      if (!text) {
        return
      }

      const { onStart, onEnd, onError } = callbacks

      const synth = window.speechSynthesis
      synth.cancel()

      // Safety: Load voices if not ready yet
      let voiceToUse = currentVoice
      if (!voiceToUse) {
        const voices = await getVoices()
        voiceToUse = selectBestVoice(voices, scriptType)
        if (isMounted.current) setCurrentVoice(voiceToUse)
      }

      const buildUtterance = (voice: SpeechSynthesisVoice | null) => {
        const u = new SpeechSynthesisUtterance(text)
        if (voice) {
          u.voice = voice
          u.lang = voice.lang
        } else {
          u.lang = scriptType === 'traditional' ? 'zh-TW' : 'zh-CN'
        }
        u.rate = rate
        u.pitch = 1
        u.volume = volume
        return u
      }

      const sessionId = ++playSessionRef.current
      let started = false
      let ended = false
      let retryingAlt = false
      setStuckMessage(null)

      let finishedResolve: (() => void) | null = null
      let finishedReject: ((reason?: unknown) => void) | null = null
      const finished = new Promise<void>((resolve, reject) => {
        finishedResolve = resolve
        finishedReject = reject
      })
      let settled = false
      const resolveFinished = () => {
        if (settled) return
        settled = true
        finishedResolve?.()
      }
      const rejectFinished = (reason?: unknown) => {
        if (settled) return
        settled = true
        finishedReject?.(reason)
      }

      const attachHandlers = (u: SpeechSynthesisUtterance) => {
        u.onstart = () => {
          started = true
          if (isMounted.current) setIsPlaying(true)
          setStuckMessage(null)
          onStart?.()
        }
        u.onend = () => {
          ended = true
          if (isMounted.current) setIsPlaying(false)
          utteranceRef.current = null
          resolveFinished()
          onEnd?.()
        }
        u.onerror = (e) => {
          ended = true
          if (isMounted.current) setIsPlaying(false)
          utteranceRef.current = null
          rejectFinished(e || new Error('Speech synthesis error'))
          // If it never started and wasn't just a cancel, surface a hint
          if (!started && e.error !== 'canceled' && e.error !== 'interrupted') {
            setStuckMessage(
              'Speech synthesis failed. Try restarting your browser.'
            )
          }
          onError?.(e)
        }
      }

      const speakWithVoice = (voice: SpeechSynthesisVoice | null) => {
        const utterance = buildUtterance(voice)
        attachHandlers(utterance)
        utteranceRef.current = utterance
        ;(
          window as Window & {
            speechSynthesisUtterance?: SpeechSynthesisUtterance
          }
        ).speechSynthesisUtterance = utterance // keep alive in some browsers
        if (synth.paused) {
          synth.resume()
        }
        synth.speak(utterance)
      }

      speakWithVoice(voiceToUse)
      const resumeKick = (ms: number) =>
        setTimeout(() => {
          if (playSessionRef.current !== sessionId || ended) return
          if (!started) {
            synth.resume()
          }
        }, ms)
      resumeKick(0)
      resumeKick(150)
      resumeKick(400)
      setTimeout(() => {
        if (playSessionRef.current !== sessionId || ended) return
        if (!started) {
          if (synth.speaking || synth.pending) {
            started = true
            if (isMounted.current) setIsPlaying(true)
            return
          }
          setStuckMessage('Speech may be blocked. Try restarting your browser.')
          if (retryingAlt) return
          retryingAlt = true
          ;(async () => {
            const voices = await getVoices()
            const altVoice =
              selectBestVoice(
                voices.filter((v) => v.name !== voiceToUse?.name),
                scriptType
              ) || voiceToUse
            synth.cancel()
            speakWithVoice(altVoice)
          })()
        }
      }, 900)

      const cancelHandle = () => {
        if (playSessionRef.current !== sessionId) return
        ended = true
        utteranceRef.current = null
        synth.cancel()
        if (isMounted.current) setIsPlaying(false)
        resolveFinished()
      }

      return {
        cancel: cancelHandle,
        finished
      }
    },
    [currentVoice, getVoices, selectBestVoice, scriptType, rate, volume]
  )

  return {
    play,
    stop,
    isPlaying,
    currentVoiceName: currentVoice?.name, // Useful to show user which voice is active
    isSupported: typeof window !== 'undefined' && 'speechSynthesis' in window,
    stuckMessage
  }
}
